import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNewSveaBankIdSignFlow } from "./useNewSveaBankIdSignFlow";

import type { NewSveaBankIdSignFlowResult } from "./useNewSveaBankIdSignFlow";
import type { Middleware } from "../../../services/api/client/AppMiddleware";
import type { FC, PropsWithChildren } from "react";

/*
    This hook provides Svea BankID signature functionality.

    To setup;
        * call useNewSveaSignatureProvider to get the ContextProvider and middleware.
        * register the middleware onto your fetch pipeline (appMiddleware).
        * Wrap your app in the ContextProvider.


    To use: call useNewSveaSignature to get access to the sign() function.


    The middleware intercepts 307 responses designated for svea signature. (307 with "interactionRequest" header set to "RequireSignature").
    When such a response is detected, it triggers the Svea BankID signature flow via a modal dialog.
    If the user successfully signs, the original request is retried.
    If the user cancels or fails signature, a standardized error response is returned.


    Unlike authentication which groups all requests together, signature groups requests by signingToken.
    Different signingTokens are processed sequentially - one at a time.
    Multiple requests with the same signingToken will be resolved together.

*/

type SignResult =
	| {
			status: "Succeeded";
			statusCode: number;
			content: null | string;
	  }
	| {
			status: "Cancelled";
	  };

interface NewSveaSignatureContextValue {
	sign: (signingToken: string) => Promise<SignResult>;
}

export const NewSveaSignatureContext = createContext<NewSveaSignatureContextValue>({
	sign: async (): Promise<SignResult> => {
		throw new Error("useNewSveaSignature must be used within a NewSveaSignatureProvider");
	},
});

export const useNewSveaSignature = () => use(NewSveaSignatureContext);

export interface NewSveaSignatureProvider {
	ContextProvider: FC<PropsWithChildren>;
	SignModal: FC;
	middleware: Middleware;
}

interface HandledSignModalProps {
	signingToken: string;
	content: FC<NewSveaBankIdSignFlowResult>;
	onSuccess: (statusCode: number, content: null | string) => void;
	onCancel: () => void;
}

const HandledSignModalContent: FC<HandledSignModalProps> = ({
	signingToken,
	content,
	onSuccess,
	onCancel,
}) => {
	const flowState = useNewSveaBankIdSignFlow(signingToken);
	const hasCompletedRef = useRef(false);

	useEffect(() => {
		if (hasCompletedRef.current) {
			return;
		}

		if (flowState.signState.type === "Completed") {
			hasCompletedRef.current = true;
			onSuccess(flowState.signState.statusCode, flowState.signState.content);
			return;
		}

		if (
			flowState.signState.type === "Cancelled" ||
			flowState.signState.type === "Failed" ||
			flowState.signState.type === "Error"
		) {
			hasCompletedRef.current = true;
			onCancel();
		}
	}, [flowState.signState, onCancel, onSuccess]);

	return content(flowState);
};

export const useNewSveaSignatureProvider = ({ SignModal: SignModalComponent }: { SignModal: FC<NewSveaBankIdSignFlowResult> }): NewSveaSignatureProvider => {
	const [tokenQueue, setTokenQueue] = useState<string[]>([]);
	const pendingSignResolversRef = useRef<Map<string, ((response: SignResult) => void)[]>>(new Map());

	const isSigning = useMemo(() => tokenQueue.length > 0, [tokenQueue]);
	const currentSigningToken = useMemo(() => tokenQueue[0] ?? null, [tokenQueue]);

	const sign = useCallback((signingToken: string) => {
		if (!pendingSignResolversRef.current.has(signingToken)) {
			pendingSignResolversRef.current.set(signingToken, []);
		}

		setTokenQueue(prev => {
			if (!prev.includes(signingToken)) {
				return [...prev, signingToken];
			}
			return prev;
		});

		return new Promise<SignResult>(resolve => {
			const resolvers = pendingSignResolversRef.current.get(signingToken) ?? [];
			resolvers.push(resolve);
			pendingSignResolversRef.current.set(signingToken, resolvers);
		});
	}, []);

	const onSignSuccess = useCallback((statusCode: number, content: null | string) => {
		setTokenQueue(prev => {
			const currentToken = prev[0];
			if (currentToken) {
				const resolvers = pendingSignResolversRef.current.get(currentToken) ?? [];
				resolvers.forEach(resolve => resolve({ status: "Succeeded", statusCode, content }));
				pendingSignResolversRef.current.delete(currentToken);

				return prev.slice(1);
			}
			return prev;
		});
	}, []);

	const onSignCancel = useCallback(() => {
		setTokenQueue(prev => {
			const currentToken = prev[0];
			if (currentToken) {
				const resolvers = pendingSignResolversRef.current.get(currentToken) ?? [];
				resolvers.forEach(resolve => resolve({ status: "Cancelled" }));
				pendingSignResolversRef.current.delete(currentToken);
			}

			pendingSignResolversRef.current.clear();
			return [];
		});
	}, []);

	const ContextProvider: FC<PropsWithChildren> = useMemo(
		() =>
			({ children }) => <NewSveaSignatureContext value={{ sign }}>{children}</NewSveaSignatureContext>,
		[sign],
	);

	const SignModal: FC = useMemo(
		() => () => {
			if (!isSigning || !currentSigningToken) {
				return null;
			}
			return (
				<HandledSignModalContent
					signingToken={currentSigningToken}
					content={SignModalComponent}
					onSuccess={onSignSuccess}
					onCancel={onSignCancel}
				/>
			);
		},
		[SignModalComponent, isSigning, currentSigningToken, onSignSuccess, onSignCancel],
	);

	const middleware: Middleware = fetch => async (input, options) => {
		let response = await fetch(input, options);

		if (response.status === 307 && response.headers.get("interactionRequest") === "RequireSignature") {
			const parametersString = response.headers.get("interactionRequestArguments");
			const parameters: { SigningToken: string } = !parametersString ? undefined : JSON.parse(parametersString);

			const signResponse = await sign(parameters.SigningToken);

			const sendInteractionResponse = async (payload: object) => {
				const myOptions = {
					...options,
					headers: {
						...options?.headers,
						interactionRequestId: response.headers.get("interactionRequestRequestId"),
						interactionRequestInteractionID: response.headers.get("interactionRequestInteractionId"),
						interactionRequestInteractionResponse: JSON.stringify(payload),
					},
				};
				return await fetch(input, myOptions);
			};

			if (signResponse.status === "Succeeded") {
				response = await sendInteractionResponse({
					Status: "Succeeded",
					StatusCode: signResponse.statusCode,
					Content: signResponse.content,
				});
			} else if (signResponse.status === "Cancelled") {
				response = await sendInteractionResponse({
					Status: "Cancelled",
				});
			}
		}

		return response;
	};

	return {
		ContextProvider,
		SignModal,
		middleware,
	};
};
